import React, { useState } from 'react';
import { Filter, RefreshCw, Calendar, Search, SlidersHorizontal } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Separator } from '../ui/separator';
import { 
  AccrualFilters as AccrualFiltersType,
  AccrualStatus,
  AccrualPeriod,
  getCurrentPeriod,
  getPeriodFromDate,
  formatPeriod,
  DEFAULT_CATEGORIES,
  ACCRUAL_STATUS_LABELS
} from '../../types/accrual';

interface AccrualFiltersProps {
  filters: AccrualFiltersType;
  onFiltersChange: (filters: AccrualFiltersType) => void;
  loading?: boolean;
  onRefresh?: () => void;
}

export function AccrualFilters({ filters, onFiltersChange, loading, onRefresh }: AccrualFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [tempFilters, setTempFilters] = useState<AccrualFiltersType>(filters);

  // Generate period options (last 24 months + next 12 months)
  const generatePeriodOptions = (): AccrualPeriod[] => {
    const periods: AccrualPeriod[] = [];
    const currentDate = new Date();
    
    // Past periods (24 months)
    for (let i = 23; i >= 0; i--) {
      const date = new Date(currentDate);
      date.setMonth(date.getMonth() - i);
      periods.push(getPeriodFromDate(date));
    }
    
    // Future periods (12 months)
    for (let i = 1; i <= 12; i++) {
      const date = new Date(currentDate);
      date.setMonth(date.getMonth() + i);
      periods.push(getPeriodFromDate(date));
    }
    
    return periods;
  };

  const periodOptions = generatePeriodOptions();

  const handlePeriodChange = (value: string) => {
    const [month, year] = value.split('/').map(Number);
    const period = periodOptions.find(p => p.month === month && p.year === year);
    
    if (period) {
      const newFilters = { ...filters, period };
      setTempFilters(newFilters);
      onFiltersChange(newFilters);
    }
  };

  const handleStatusChange = (status: AccrualStatus, checked: boolean) => {
    const newStatus = checked 
      ? [...(filters.status || []), status]
      : (filters.status || []).filter(s => s !== status);
    
    const newFilters = { ...filters, status: newStatus };
    setTempFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleTypeChange = (type: 'receita' | 'despesa', checked: boolean) => {
    const newType = checked 
      ? [...(filters.type || []), type]
      : (filters.type || []).filter(t => t !== type);
    
    const newFilters = { ...filters, type: newType };
    setTempFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleCategoryChange = (category: string, checked: boolean) => {
    const newCategory = checked 
      ? [...(filters.category || []), category]
      : (filters.category || []).filter(c => c !== category);
    
    const newFilters = { ...filters, category: newCategory };
    setTempFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleSearchChange = (value: string) => {
    const newFilters = { ...filters, client_search: value || undefined };
    setTempFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleAmountChange = (field: 'amount_min' | 'amount_max', value: string) => {
    const numericValue = value ? parseFloat(value) : undefined;
    const newFilters = { ...filters, [field]: numericValue };
    setTempFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleSortChange = (field: 'sort_by' | 'sort_direction', value: string) => {
    const newFilters = { ...filters, [field]: value };
    setTempFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleToggleChange = (field: 'show_recurring_only' | 'show_overdue_only', checked: boolean) => {
    const newFilters = { ...filters, [field]: checked || undefined };
    setTempFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearFilters = () => {
    const defaultFilters: AccrualFiltersType = {
      period: getCurrentPeriod(),
      status: Object.values(AccrualStatus),
      type: ['receita', 'despesa'],
      sort_by: 'competence_date',
      sort_direction: 'desc'
    };
    setTempFilters(defaultFilters);
    onFiltersChange(defaultFilters);
  };

  const activeFiltersCount = [
    filters.client_search,
    filters.amount_min,
    filters.amount_max,
    filters.category && filters.category.length < Object.values(DEFAULT_CATEGORIES).flat().length ? filters.category : null,
    filters.show_recurring_only,
    filters.show_overdue_only
  ].filter(Boolean).length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <CardTitle className="text-lg">Filtros</CardTitle>
            {activeFiltersCount > 0 && (
              <Badge variant="secondary">{activeFiltersCount}</Badge>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" size="sm">
                  <SlidersHorizontal className="h-4 w-4" />
                  {isExpanded ? 'Menos' : 'Mais'} Filtros
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Basic Filters */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Period Selection */}
          <div className="space-y-2">
            <Label>Período de Competência</Label>
            <Select
              value={filters.period ? `${filters.period.month}/${filters.period.year}` : ''}
              onValueChange={handlePeriodChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o período" />
              </SelectTrigger>
              <SelectContent>
                {periodOptions.map(period => (
                  <SelectItem key={`${period.month}/${period.year}`} value={`${period.month}/${period.year}`}>
                    {formatPeriod(period)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Search */}
          <div className="space-y-2">
            <Label>Buscar</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cliente, fornecedor ou descrição..."
                value={filters.client_search || ''}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Sort By */}
          <div className="space-y-2">
            <Label>Ordenar por</Label>
            <Select
              value={filters.sort_by || 'competence_date'}
              onValueChange={(value) => handleSortChange('sort_by', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="competence_date">Data de Competência</SelectItem>
                <SelectItem value="due_date">Data de Vencimento</SelectItem>
                <SelectItem value="amount">Valor</SelectItem>
                <SelectItem value="status">Status</SelectItem>
                <SelectItem value="category">Categoria</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sort Direction */}
          <div className="space-y-2">
            <Label>Ordem</Label>
            <Select
              value={filters.sort_direction || 'desc'}
              onValueChange={(value) => handleSortChange('sort_direction', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asc">Crescente</SelectItem>
                <SelectItem value="desc">Decrescente</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <CollapsibleContent className="space-y-4">
            <Separator />

            {/* Type Filter */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Tipo de Lançamento</Label>
              <div className="flex flex-wrap gap-3">
                {[
                  { value: 'receita', label: 'Receitas' },
                  { value: 'despesa', label: 'Despesas' }
                ].map(type => (
                  <div key={type.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`type-${type.value}`}
                      checked={filters.type?.includes(type.value as 'receita' | 'despesa') || false}
                      onCheckedChange={(checked) => 
                        handleTypeChange(type.value as 'receita' | 'despesa', !!checked)
                      }
                    />
                    <Label htmlFor={`type-${type.value}`} className="text-sm font-normal">
                      {type.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Status Filter */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Status</Label>
              <div className="flex flex-wrap gap-3">
                {Object.values(AccrualStatus).map(status => (
                  <div key={status} className="flex items-center space-x-2">
                    <Checkbox
                      id={`status-${status}`}
                      checked={filters.status?.includes(status) || false}
                      onCheckedChange={(checked) => handleStatusChange(status, !!checked)}
                    />
                    <Label htmlFor={`status-${status}`} className="text-sm font-normal">
                      {ACCRUAL_STATUS_LABELS[status]}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Category Filter */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Categorias</Label>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Receitas</Label>
                  <div className="mt-2 space-y-2">
                    {DEFAULT_CATEGORIES.receita.map(category => (
                      <div key={category} className="flex items-center space-x-2">
                        <Checkbox
                          id={`category-${category}`}
                          checked={filters.category?.includes(category) || false}
                          onCheckedChange={(checked) => handleCategoryChange(category, !!checked)}
                        />
                        <Label htmlFor={`category-${category}`} className="text-sm font-normal">
                          {category}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Despesas</Label>
                  <div className="mt-2 space-y-2">
                    {DEFAULT_CATEGORIES.despesa.map(category => (
                      <div key={category} className="flex items-center space-x-2">
                        <Checkbox
                          id={`category-${category}`}
                          checked={filters.category?.includes(category) || false}
                          onCheckedChange={(checked) => handleCategoryChange(category, !!checked)}
                        />
                        <Label htmlFor={`category-${category}`} className="text-sm font-normal">
                          {category}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Amount Range */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Valor Mínimo (R$)</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={filters.amount_min || ''}
                  onChange={(e) => handleAmountChange('amount_min', e.target.value)}
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="space-y-2">
                <Label>Valor Máximo (R$)</Label>
                <Input
                  type="number"
                  placeholder="999999.99"
                  value={filters.amount_max || ''}
                  onChange={(e) => handleAmountChange('amount_max', e.target.value)}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            {/* Toggle Options */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Opções Especiais</Label>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="show-recurring"
                    checked={filters.show_recurring_only || false}
                    onCheckedChange={(checked) => handleToggleChange('show_recurring_only', !!checked)}
                  />
                  <Label htmlFor="show-recurring" className="text-sm font-normal">
                    Mostrar apenas lançamentos recorrentes
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="show-overdue"
                    checked={filters.show_overdue_only || false}
                    onCheckedChange={(checked) => handleToggleChange('show_overdue_only', !!checked)}
                  />
                  <Label htmlFor="show-overdue" className="text-sm font-normal">
                    Mostrar apenas lançamentos em atraso
                  </Label>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Clear Filters */}
        {activeFiltersCount > 0 && (
          <div className="flex justify-end pt-2">
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Limpar Filtros
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}